import { Button, Checkbox, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Banner } from "components/common/Banner";
import { NiraDialog } from "components/common/NiraDialog";
import { useStageConfig } from "in_queue/contexts/ClusterContext";
import { CostAssumption } from "in_queue/types/scenarioType";
import { useState } from "react";
import { scenarioCount } from "../ScenarioRerun/bulkRerunToast";
import css from "./BulkScenarioCreateDialog.module.scss";
import { bulkScenarioCreateSummary } from "./bulkScenarioCreateSummary";
import { BulkScenarioDraft } from "./bulkScenarioDrafts";
import {
    BulkScenarioUploadLabel,
    createScenariosLabel
} from "./bulkScenarioUploadLabels";
import { useBulkScenarioSize } from "./useBulkScenarioSize";

/** Confirm creating the upload, blocking it over the scenario limit or until removed generation is acknowledged. */
export const BulkScenarioCreateDialog: React.FC<{
    readonly drafts: ReadonlyArray<BulkScenarioDraft>;
    readonly defaultCostAssumptions: CostAssumption;
    readonly remainingScenarioSlots: number | undefined;
    readonly overLimitCount: number;
    readonly isCreating: boolean;
    readonly onClose: () => void;
    readonly onCreate: () => void;
}> = ({
    drafts,
    defaultCostAssumptions,
    remainingScenarioSlots,
    overLimitCount,
    isCreating,
    onClose,
    onCreate
}) => {
    const { maxMwPercentageRemoved } = useStageConfig();
    const sizeOf = useBulkScenarioSize();
    const [isThresholdAcknowledged, setIsThresholdAcknowledged] =
        useState(false);

    const overThresholdTitles = drafts
        .filter((draft) => sizeOf(draft).exceedsMwThreshold)
        .map((draft) => draft.title);
    const isOverLimit =
        overLimitCount > 0 && remainingScenarioSlots !== undefined;
    const isOverThreshold = overThresholdTitles.length > 0;
    const label = createScenariosLabel(drafts.length);

    return (
        <NiraDialog
            isOpen
            onClose={onClose}
            isCloseDisabled={isCreating}
            eyebrow={BulkScenarioUploadLabel.Eyebrow}
            title={label}
            className={css["BulkScenarioCreateDialog"]}
            gap="small"
            footerActions={
                <>
                    <Button
                        text="Cancel"
                        onClick={onClose}
                        disabled={isCreating}
                        size="large"
                    />
                    <Button
                        text={label}
                        intent={Intent.PRIMARY}
                        loading={isCreating}
                        disabled={
                            isOverLimit ||
                            (isOverThreshold && !isThresholdAcknowledged)
                        }
                        onClick={onCreate}
                        size="large"
                    />
                </>
            }
        >
            <Banner
                intent={Intent.PRIMARY}
                icon={IconNames.INFO_SIGN}
                title={`${scenarioCount(drafts.length)} will be created`}
                description={bulkScenarioCreateSummary(
                    drafts,
                    defaultCostAssumptions
                ).map((line) => (
                    <span
                        key={line}
                        className={css["BulkScenarioCreateDialog-summary-line"]}
                    >
                        {line}
                    </span>
                ))}
            />
            {isOverLimit && (
                <Banner
                    intent={Intent.DANGER}
                    icon={IconNames.ERROR}
                    title="This is more than your scenario limit"
                    description={`This cluster has room for ${scenarioCount(
                        remainingScenarioSlots
                    )}. Remove files or archive scenarios to make room.`}
                />
            )}
            {isOverThreshold && (
                <Banner
                    intent={Intent.WARNING}
                    icon={IconNames.WARNING_SIGN}
                    title={`${scenarioCount(overThresholdTitles.length)} ${
                        overThresholdTitles.length === 1 ? "removes" : "remove"
                    } ${maxMwPercentageRemoved * 100}% or more of the queue`}
                    description={
                        <>
                            <b>{overThresholdTitles.join(", ")}</b>. Scenarios
                            that drop this much generation will likely fail
                            because of issues with redispatching it.
                            <Checkbox
                                className={
                                    css["BulkScenarioCreateDialog-acknowledge"]
                                }
                                checked={isThresholdAcknowledged}
                                disabled={isCreating}
                                label="Acknowledge"
                                onChange={() =>
                                    setIsThresholdAcknowledged(
                                        !isThresholdAcknowledged
                                    )
                                }
                            />
                        </>
                    }
                />
            )}
        </NiraDialog>
    );
};
